import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, Spinner, Copy, ArrowsClockwise, Microphone,
  FileText, Eye, ShieldCheck, PencilSimple, Trash,
} from "@phosphor-icons/react";
import SectionOnboarding from "@/components/SectionOnboarding";
import { usePropertyDetail } from "../context";

export default function DescrizioneTab() {
  const {
    property, setProperty, generating, onGenerateDescription, tone, setTone, length, setLength, setTab, id, tab,
    voiceRecording, voiceTranscript, setVoiceTranscript, voiceUploading,
    onStartRecording, onStopRecording,
  } = usePropertyDetail();
  // iter v28 r24 — Nota scritta come alternativa alla nota vocale. Tendina
  // toggle tra "voce" e "scritta". La nota scritta viene salvata nel campo
  // `voice_transcript` (stesso campo della trascrizione) così la rigenerazione
  // del testo la pesca automaticamente.
  const [noteMode, setNoteMode] = useState("voice");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDraft, setNoteDraft] = useState(voiceTranscript || "");
  React.useEffect(() => { setNoteDraft(voiceTranscript || ""); }, [voiceTranscript]);

  // iter v28 r60 — Auto-save descrizione: la textarea ora persiste sul server
  // con debounce 800ms + indicatore visivo "Salvato · Salvataggio… · Non salvato".
  // Prima il testo si modificava solo nello state locale e si perdeva al
  // cambio tab. Adesso ogni edit dell'agente viene salvato senza click.
  const [descSaveState, setDescSaveState] = useState("idle"); // idle | dirty | saving | saved | error
  const lastSavedRef = React.useRef(property?.ai_description || "");
  const debounceRef = React.useRef(null);
  React.useEffect(() => {
    // Quando il property cambia da fuori (rigenerazione), aggiorna ref senza marcare dirty
    lastSavedRef.current = property?.ai_description || "";
  }, [property?.id]); // solo al cambio annuncio

  const saveDescription = async (text) => {
    setDescSaveState("saving");
    try {
      await api.put(`/properties/${id}`, { ai_description: text });
      lastSavedRef.current = text;
      setDescSaveState("saved");
      // Auto-fade "Salvato" dopo 1.5s
      setTimeout(() => setDescSaveState((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (e) {
      setDescSaveState("error");
      toast.error("Salvataggio non riuscito · controlla connessione");
    }
  };

  const onDescChange = (e) => {
    const next = e.target.value;
    setProperty((p) => ({ ...p, ai_description: next }));
    if (next === lastSavedRef.current) {
      setDescSaveState("idle");
      return;
    }
    setDescSaveState("dirty");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => saveDescription(next), 800);
  };

  const saveWrittenNote = async () => {
    if (!noteDraft.trim()) return;
    setNoteSaving(true);
    try {
      await api.put(`/properties/${id}`, { voice_transcript: noteDraft.trim() });
      setVoiceTranscript(noteDraft.trim());
      setProperty((p) => ({ ...p, voice_transcript: noteDraft.trim() }));
      toast.success("Nota salvata · ora puoi rigenerare il testo qui sotto");
    } catch (err) {
      toast.error("Errore salvataggio nota");
    } finally { setNoteSaving(false); }
  };

  // iter v28 r76 — Elimina nota (vocale o scritta). L'agente può sbagliare a
  // dire qualcosa e deve poterla rimuovere. Svuota anche noteDraft per
  // resettare visivamente la textarea.
  const [deletingNote, setDeletingNote] = useState(false);
  const deleteNote = async () => {
    if (deletingNote) return;
    if (!window.confirm("Eliminare la nota? Il testo dell'annuncio resterà invariato.")) return;
    setDeletingNote(true);
    try {
      await api.put(`/properties/${id}`, { voice_transcript: "" });
      setVoiceTranscript("");
      setNoteDraft("");
      setProperty((p) => ({ ...p, voice_transcript: "" }));
      toast.success("Nota eliminata");
    } catch (err) {
      toast.error("Impossibile eliminare la nota");
    } finally { setDeletingNote(false); }
  };
  return (
    <>
      {/* Mini onboarding "Annuncio in 3 passi" — visibile solo la prima volta */}
      <SectionOnboarding
        storageKey="annuncio:3-passi"
        overline="L'annuncio in 3 passi"
        title={<>Da dati a <span className="italic-accent text-[#7C8B6E]">testo pronto</span> · senza errori</>}
        subtitle="Crei il testo, lo controlli con un colpo d'occhio, poi correggi gli errori che gli umani non vedono. Tocca una card per partire da lì."
        steps={[
          {
            key: "descrizione",
            Icon: FileText,
            overline: "1 · Testo",
            title: "Scrivi la descrizione",
            body: "Scegli tono e lunghezza · Pronto compone il testo dai dati dell'immobile.",
            accent: "#7C8B6E",
            onClick: () => setTab("descrizione"),
          },
          {
            key: "anteprima",
            Icon: Eye,
            overline: "2 · Anteprima",
            title: "Vedi come appare",
            body: "Anteprima vetrina, social e portali — tutto in un colpo d'occhio.",
            accent: "#C49A2A",
            onClick: () => setTab("anteprima"),
          },
          {
            key: "autopilot",
            Icon: ShieldCheck,
            overline: "3 · Controllo qualità",
            title: "Correggi gli errori",
            body: "Refusi, dati incoerenti, frasi non chiare · trovati e suggeriti.",
            accent: "#8B5E7C",
            onClick: () => setTab("autopilot"),
          },
        ]}
      />
      {/* iter v28 r20 — Banner chiarezza: quando non c'è ancora descrizione,
          mostro all'utente quali dati ho già raccolto e userò per generare
          il testo. Così è IMMEDIATAMENTE chiaro che il sistema NON parte
          da zero e che i dati che ha già dato/raccolto contano davvero. */}
      {!property?.ai_description && (
        <div className="mt-6 p-4 bg-gradient-to-br from-[#F1F4ED] to-[#FAFAF7] border border-[#7C8B6E]/30 rounded-lg" data-testid="generate-context-banner">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-[#7C8B6E] text-white flex items-center justify-center flex-shrink-0">
              <Sparkle size={15} weight="fill" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[13.5px] font-semibold text-[#1C1C1A] leading-tight">
                Userò i dati dell'immobile per scrivere il testo
              </div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {[
                  property?.property_type && `${property.property_type}`,
                  property?.surface && `${property.surface} m²`,
                  property?.rooms && `${property.rooms} locali`,
                  property?.bathrooms && `${property.bathrooms} bagni`,
                  property?.city,
                  property?.address,
                  property?.floor && `Piano ${property.floor}`,
                  property?.energy_class && `Classe ${property.energy_class}`,
                  property?.features?.length > 0 && `${property.features.length} caratteristiche`,
                  property?.photos_count > 0 && `${property.photos_count} foto`,
                ].filter(Boolean).slice(0, 8).map((chip, i) => (
                  <span key={i} className="inline-flex items-center text-[11px] px-2 py-0.5 rounded-full bg-white border border-[#E5E7E0] text-[#1C1C1A] font-medium">
                    {chip}
                  </span>
                ))}
              </div>
              <div className="text-[11.5px] text-[#6B6A65] mt-2 leading-snug">
                Tu scegli tono e lunghezza qui sotto · io compongo il testo.
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="card-flat p-6">
            <span className="overline">Annuncio</span>
            <h3 className="font-editorial text-2xl mt-2 mb-2">
              {property?.ai_description ? "Modifica descrizione" : "Carica descrizione"}
            </h3>
            <p className="text-[12.5px] text-[#6B6A65] mb-5 leading-relaxed">
              {property?.ai_description
                ? "Cambia tono o lunghezza e rigenera. Il testo torna pronto da copiare."
                : "Pronto scrive una descrizione adatta a portali e annunci usando i dati dell'immobile (tipologia, mq, locali, zona, caratteristiche). Scegli tono e lunghezza."}
            </p>

            <label className="overline block mb-2">Tono</label>
            <div className="grid grid-cols-2 gap-2 mb-5" data-testid="tone-buttons">
              {[
                { v: "professionale", l: "Professionale" },
                { v: "caloroso",      l: "Caloroso" },
                { v: "lussuoso",      l: "Lussuoso" },
                { v: "essenziale",    l: "Essenziale" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setTone(opt.v)}
                  className={`text-[12.5px] px-3 py-2.5 rounded-full border transition-all ${
                    tone === opt.v
                      ? "bg-[#1C1C1A] text-white border-[#1C1C1A] font-semibold"
                      : "bg-white text-[#3A3A36] border-[#E5E7E0] hover:border-[#7C8B6E]"
                  }`}
                  data-testid={`tone-btn-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>

            <label className="overline block mb-2">Lunghezza</label>
            <div className="grid grid-cols-3 gap-2 mb-6" data-testid="length-buttons">
              {[
                { v: "corta", l: "Corta" },
                { v: "media", l: "Media" },
                { v: "lunga", l: "Lunga" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setLength(opt.v)}
                  className={`text-[12.5px] px-3 py-2.5 rounded-full border transition-all ${
                    length === opt.v
                      ? "bg-[#1C1C1A] text-white border-[#1C1C1A] font-semibold"
                      : "bg-white text-[#3A3A36] border-[#E5E7E0] hover:border-[#7C8B6E]"
                  }`}
                  data-testid={`length-btn-${opt.v}`}
                >
                  {opt.l}
                </button>
              ))}
            </div>

            <button onClick={onGenerateDescription} disabled={generating} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="generate-description-btn">
              {generating ? (<><Spinner size={16} className="animate-spin" /> Generazione...</>) : (<><Sparkle size={16} weight="fill" /> {property?.ai_description ? "Rigenera descrizione" : "Genera descrizione"}</>)}
            </button>
          </div>
        </div>

        <div className="lg:col-span-8">
          <div className="card-flat p-8 min-h-[60vh]">
            {property?.ai_description ? (
              <>
                {/* iter 261 (r6 313): blocco "Materiale di partenza" — se c'è
                    la trascrizione vocale lo mostra qui in alto così l'agente
                    capisce DA DOVE è stato generato il testo.
                    iter 313 r6 — bug fix: "Modifica input" rimandava al tab
                    "foto" (Carica) invece che al tab "dimmi" (Racconta) dove
                    c'è il registratore vocale. Rimossa anche la riga italic
                    con la trascrizione completa: l'agente la rivede nel tab
                    Racconta, qui basta solo dichiarare l'origine. */}
                {property?.voice_transcript && (
                  <div
                    className="mb-5 px-3 py-2 rounded-md bg-[#F1F4ED] border border-[#7C8B6E]/20 flex items-center justify-between gap-3"
                    data-testid="annuncio-source-input"
                  >
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#7C8B6E]">
                      Generato dalla nota vocale
                    </span>
                    <button
                      type="button"
                      onClick={() => setTab("dimmi")}
                      className="text-[11px] text-[#7C8B6E] hover:underline font-semibold whitespace-nowrap"
                      data-testid="annuncio-back-to-input"
                    >
                      Modifica input →
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                  <span className="overline">Descrizione</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={onGenerateDescription}
                      disabled={generating}
                      className="btn-outline text-sm px-3 py-2 disabled:opacity-60"
                      data-testid="regen-description-inline-btn"
                      title="Rigenera con il tono/lunghezza scelti a sinistra"
                    >
                      {generating ? <><Spinner size={14} className="animate-spin" /> Rigenero…</> : <><ArrowsClockwise size={14} weight="bold" /> Rigenera</>}
                    </button>
                    <button
                      onClick={async () => {
                        await navigator.clipboard.writeText(property.ai_description);
                        toast.success("Descrizione copiata");
                      }}
                      className="btn-outline text-sm px-4 py-2"
                      data-testid="copy-description-btn"
                    >
                      <Copy size={14} /> Copia
                    </button>
                  </div>
                </div>
                <textarea
                  data-vista
                  value={property.ai_description}
                  onChange={onDescChange}
                  className="w-full min-h-[50vh] leading-relaxed text-[16px]"
                  data-testid="description-textarea"
                />
                {/* iter v28 r60 — Indicatore stato salvataggio */}
                <div className="mt-2 flex items-center gap-2 text-[11.5px]" data-testid="description-save-state">
                  {descSaveState === "idle" && <span className="text-[#9A968B]">Le modifiche vengono salvate in automatico.</span>}
                  {descSaveState === "dirty" && (
                    <span className="inline-flex items-center gap-1.5 text-[#9A6B00]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#C49A2A]" /> Modifiche non salvate…
                    </span>
                  )}
                  {descSaveState === "saving" && (
                    <span className="inline-flex items-center gap-1.5 text-[#6B6A65]">
                      <Spinner size={11} className="animate-spin" /> Salvataggio in corso…
                    </span>
                  )}
                  {descSaveState === "saved" && (
                    <span className="inline-flex items-center gap-1.5 text-[#7C8B6E] font-medium">
                      <ShieldCheck size={12} weight="fill" /> Salvato
                    </span>
                  )}
                  {descSaveState === "error" && (
                    <button onClick={() => saveDescription(property.ai_description)} className="inline-flex items-center gap-1.5 text-red-600 underline font-medium" data-testid="description-save-retry">
                      Salvataggio non riuscito · riprova
                    </button>
                  )}
                </div>
                <p className="mt-3 text-xs text-[#6B6A65]">Puoi modificare il testo liberamente prima di copiarlo.</p>

                {/* iter v28 r24 — Box "Aggiungi una nota" sotto il testo.
                    Toggle voce / scritta. La nota va a popolare voice_transcript
                    che viene usato da onGenerateDescription per rigenerare il
                    testo tenendone conto. Prima questo box viveva nell'Autopilot
                    (Controllo qualità) ma l'utente non capiva il flow, era più
                    naturale tenerlo qui sotto il testo da migliorare. */}
                <div className="mt-6 p-4 bg-[#FAF9F4] border border-[#E5E7E0] rounded-md" data-testid="annuncio-nota-block">
                  <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13.5px] font-semibold text-[#1C1C1A] leading-tight">
                        Aggiungi una nota all'annuncio
                      </div>
                      <div className="text-[11.5px] text-[#6B6A65] leading-snug mt-0.5">
                        Peculiarità, richieste del venditore, dettagli da non perdere. Dopo, rigenera il testo qui sotto.
                      </div>
                    </div>
                    <div className="inline-flex rounded-full bg-white border border-[#E5E7E0] p-0.5">
                      <button
                        onClick={() => setNoteMode("voice")}
                        className={`px-3 py-1 rounded-full text-[11.5px] font-medium transition-colors ${noteMode === "voice" ? "bg-[#7C8B6E] text-white" : "text-[#6B6A65]"}`}
                        data-testid="note-mode-voice"
                      >
                        <Microphone size={11} weight="bold" className="inline mr-1" /> Voce
                      </button>
                      <button
                        onClick={() => setNoteMode("text")}
                        className={`px-3 py-1 rounded-full text-[11.5px] font-medium transition-colors ${noteMode === "text" ? "bg-[#7C8B6E] text-white" : "text-[#6B6A65]"}`}
                        data-testid="note-mode-text"
                      >
                        <PencilSimple size={11} weight="bold" className="inline mr-1" /> Scrivi
                      </button>
                    </div>
                  </div>

                  {noteMode === "voice" ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="annuncio-voice-recorder">
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <Microphone size={20} weight={voiceRecording ? "fill" : "regular"} className={voiceRecording ? "text-[#C24E4E] animate-pulse" : "text-[#7C8B6E]"} />
                        <div className="text-[12.5px] text-[#1C1C1A] min-w-0">
                          {voiceRecording ? "Sto registrando · parla normalmente" : voiceUploading ? "Trascrivo…" : voiceTranscript ? "Nota già registrata · puoi sovrascriverla" : "Premi per registrare la tua nota a voce"}
                        </div>
                      </div>
                      {voiceRecording ? (
                        <button onClick={onStopRecording} className="px-3.5 py-1.5 rounded-full bg-[#C24E4E] text-white text-[12px] font-semibold" data-testid="annuncio-voice-stop">Ferma</button>
                      ) : (
                        <button onClick={onStartRecording} disabled={voiceUploading} className="px-3.5 py-1.5 rounded-full bg-[#1C1C1A] hover:bg-black text-white text-[12px] font-semibold disabled:opacity-50" data-testid="annuncio-voice-start">
                          {voiceTranscript ? "Sovrascrivi" : "Registra"}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2.5" data-testid="annuncio-written-note">
                      <textarea
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        rows={3}
                        placeholder="Es. La cucina è di Boffi, ristrutturazione del 2023, vista parco al tramonto…"
                        className="w-full px-3 py-2 text-[13.5px] border border-[#E5E7E0] rounded-md bg-white focus:outline-none focus:border-[#7C8B6E]"
                        data-testid="annuncio-note-textarea"
                      />
                      <div className="flex items-center justify-end">
                        <button
                          onClick={saveWrittenNote}
                          disabled={noteSaving || !noteDraft.trim() || noteDraft.trim() === (voiceTranscript || "").trim()}
                          className="px-3.5 py-1.5 rounded-full bg-[#1C1C1A] hover:bg-black text-white text-[12px] font-semibold disabled:opacity-50"
                          data-testid="annuncio-note-save"
                        >
                          {noteSaving ? "Salvo…" : "Salva nota"}
                        </button>
                      </div>
                    </div>
                  )}

                  {voiceTranscript && (
                    <>
                      <div
                        className="mt-3 flex items-start justify-between gap-2 text-[12.5px] italic text-[#1C1C1A] bg-white p-2.5 border border-[#E5E7E0] rounded-sm"
                        data-testid="annuncio-current-note"
                      >
                        <span className="flex-1 min-w-0">"{voiceTranscript}"</span>
                        <button
                          type="button"
                          onClick={deleteNote}
                          disabled={deletingNote}
                          className="flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-[#9A968B] hover:text-[#C24E4E] hover:bg-[#FBEEEE] transition-colors disabled:opacity-50"
                          aria-label="Elimina nota"
                          title="Elimina nota"
                          data-testid="annuncio-note-delete"
                        >
                          <Trash size={13} weight="bold" />
                        </button>
                      </div>
                      <button
                        onClick={onGenerateDescription}
                        disabled={generating}
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#7C8B6E] hover:bg-[#6E7C61] text-white text-[13px] font-semibold disabled:opacity-60 transition-colors"
                        data-testid="annuncio-regen-with-note-btn"
                      >
                        {generating
                          ? <><Spinner size={14} className="animate-spin" /> Rigenero il testo…</>
                          : <><Sparkle size={14} weight="fill" /> Rigenera il testo tenendo conto della nota</>}
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center py-24">
                <FileText size={40} weight="light" className="text-[#7C8B6E]" />
                <h3 className="font-editorial text-2xl sm:text-3xl mt-4">Niente testo ancora</h3>
                <p className="mt-2 text-[#6B6A65] max-w-sm">
                  Tocca <strong className="text-[#1C1C1A]">"Genera descrizione"</strong> qui a sinistra. Pronto ti scrive una descrizione pronta da pubblicare in pochi secondi.
                </p>
              </div>
            )}
          </div>

          {/* iter 261: blocco "Pagina pubblica" RIMOSSO dal tab Annuncio.
              La pubblicazione è l'ultimo passo del flow (Contenuti →
              Annuncio → Materiali → Promuovi) — qui non ha senso. La
              pagina pubblica si attiva dal tab Promuovi, dopo aver
              rivisto tutto. */}
        </div>
      </div>
    </>
  );
}
